import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';

type JobRow = {
  id: string;
  user_id: string | null;
  category_set_id: string | null;
  input_data: any;
  total_count?: number | null;
  status: 'queued' | 'processing' | 'done' | 'failed' | string;
  created_at: string | null;
  updated_at: string | null;
};

type CategorySetRow = {
  id: string;
  user_id: string | null;
  name: string;
  categories: string[];
  created_at: string | null;
};

const Card: React.FC<{ title: string; value: string; hint?: string }> = ({ title, value, hint }) => (
  <div style={{
    border: '1px solid var(--color-border)',
    borderRadius: '12px',
    padding: '16px',
    background: 'linear-gradient(180deg, rgba(84,101,255,0.08), rgba(0,0,0,0) 60%), var(--color-surface)'
  }}>
    <div style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 28, fontWeight: 800 }}>{value}</div>
    {hint && <div style={{ color: 'var(--color-text-muted)', marginTop: 4, fontSize: 12 }}>{hint}</div>}
  </div>
);

const Dashboard: React.FC = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobRow[] | null>(null);
  const [sets, setSets] = useState<CategorySetRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr) throw userErr;
        const uid = userData.user?.id || null;
        if (!uid) {
          setError('Please log in to view your dashboard.');
          setLoading(false);
          return;
        }
        if (!mounted) return;
        setUserId(uid);

        const [jobsRes, setsRes] = await Promise.all([
          supabase
            .from('jobs')
            .select('*')
            .eq('user_id', uid)
            .order('created_at', { ascending: false })
            .limit(50),
          supabase
            .from('category_sets')
            .select('id, user_id, name, categories, created_at')
            .eq('user_id', uid)
            .order('created_at', { ascending: false })
            .limit(50),
        ]);

        if (jobsRes.error) throw jobsRes.error;
        if (setsRes.error) throw setsRes.error;
        if (!mounted) return;
        setJobs(jobsRes.data || []);
        setSets(setsRes.data || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load data');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel('realtime-jobs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, (payload) => {
        const newRow = (payload.new as any) as JobRow | null;
        const oldRow = (payload.old as any) as JobRow | null;
        const affectedUserId = newRow?.user_id ?? oldRow?.user_id ?? null;
        if (affectedUserId !== userId) return;
        setJobs((prev) => {
          const current = prev ? [...prev] : [];
          if (payload.eventType === 'INSERT' && newRow) {
            const exists = current.findIndex((j) => j.id === newRow.id);
            if (exists >= 0) current[exists] = newRow;
            else current.unshift(newRow);
          } else if (payload.eventType === 'UPDATE' && newRow) {
            const idx = current.findIndex((j) => j.id === newRow.id);
            if (idx >= 0) current[idx] = newRow;
          } else if (payload.eventType === 'DELETE' && oldRow) {
            const idx = current.findIndex((j) => j.id === oldRow.id);
            if (idx >= 0) current.splice(idx, 1);
          }
          // Keep sorted by created_at desc and limited
          current.sort((a, b) => (new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()));
          return current.slice(0, 50);
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const kpi = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const jobsThisWeek = (jobs || []).filter(j => j.created_at && new Date(j.created_at) >= weekAgo).length;

    const doneJobs = (jobs || []).filter(j => j.status === 'done' && j.created_at && j.updated_at);
    const avgSeconds = (() => {
      if (doneJobs.length === 0) return null;
      const totalMs = doneJobs.reduce((sum, j) => {
        const start = new Date(j.created_at as string).getTime();
        const end = new Date(j.updated_at as string).getTime();
        return sum + Math.max(0, end - start);
      }, 0);
      return totalMs / doneJobs.length / 1000;
    })();

    const activeSets = (sets || []).length;

    return {
      jobsThisWeek,
      avgProcessing: avgSeconds !== null ? `${avgSeconds.toFixed(1)}s` : '—',
      accuracy: '—',
      activeSets,
    };
  }, [jobs, sets]);

  const recent = useMemo(() => {
    const sorted = (jobs || []).slice().sort((a, b) => (new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()));
    return sorted.slice(0, 5);
  }, [jobs]);

  const getItemsCount = (input: any, total?: number | null): number => {
    if (typeof total === 'number') return total || 0;
    if (Array.isArray(input)) return input.length;
    if (input && Array.isArray(input.data)) return input.data.length;
    return 0;
  };

  const statusChipStyle = (status: string): React.CSSProperties => {
    const s = (status || '').toLowerCase();
    if (s === 'done') {
      return { background: 'rgba(46,204,113,0.15)', border: '1px solid rgba(46,204,113,0.45)', color: '#9be564', padding: '4px 8px', borderRadius: 999 };
    }
    if (s === 'failed') {
      return { background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.45)', color: '#ff6b6b', padding: '4px 8px', borderRadius: 999 };
    }
    if (s === 'queued') {
      return { background: 'rgba(233,235,158,0.20)', border: '1px solid rgba(233,235,158,0.55)', color: '#E9EB9E', padding: '4px 8px', borderRadius: 999 };
    }
    // processing or unknown
    return { background: 'rgba(84,101,255,0.15)', border: '1px solid rgba(84,101,255,0.45)', color: '#788BFF', padding: '4px 8px', borderRadius: 999 };
  };

  if (loading) return <div>Loading dashboard…</div>;
  if (error) return <div style={{ color: '#ff6b6b' }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Card title="Jobs this week" value={String(kpi.jobsThisWeek)} />
        <Card title="Avg. processing time" value={kpi.avgProcessing} />
        <Card title="Accuracy" value={kpi.accuracy} />
        <Card title="Active category sets" value={String(kpi.activeSets)} />
      </div>

      <div style={{ marginTop: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>Recent jobs</h3>
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f111a' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Job ID</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Items</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((j) => (
                <tr key={j.id}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.id.slice(0, 8)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{getItemsCount(j.input_data, j.total_count)}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
                    <span style={statusChipStyle(j.status)}>{j.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{j.created_at ? new Date(j.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: 'var(--color-text-muted)' }}>No jobs yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 